import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon, Loader2, Check, Bike, Car } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FORMULES, VEHICLE_TYPES, OPTIONS, MOTO } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field =
  "bg-transparent border-white/15 rounded-sm h-12 text-white placeholder:text-[var(--txt-2)] focus-visible:ring-0 focus-visible:border-[var(--blue)]";

const ALL_FORMULES = [
  ...FORMULES.map((f) => ({ apiKey: f.apiKey, name: f.name, moto: false })),
  { apiKey: MOTO.apiKey, name: MOTO.name, moto: true },
];

export const Booking = ({ formule, setFormule }) => {
  const [vehicleType, setVehicleType] = useState("citadine");
  const [options, setOptions] = useState([]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [closed, setClosed] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const isMoto = formule === "MOTO";

  const price = (() => {
    let base = 0;
    if (isMoto) base = MOTO.price;
    else {
      const f = FORMULES.find((x) => x.apiKey === formule);
      base = f ? f.prices[vehicleType] || 0 : 0;
    }
    base += options.reduce((s, o) => s + (OPTIONS.find((x) => x.label === o)?.price || 0), 0);
    return base;
  })();

  const fetchSlots = useCallback(async (d) => {
    setLoadingSlots(true);
    setTime("");
    try {
      const ds = format(d, "yyyy-MM-dd");
      const { data } = await axios.get(`${API}/availability`, { params: { date: ds } });
      setClosed(data.closed);
      setSlots(data.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (date) fetchSlots(date);
  }, [date, fetchSlots]);

  const toggleOption = (label) =>
    setOptions((prev) => (prev.includes(label) ? prev.filter((o) => o !== label) : [...prev, label]));

  const submit = async (e) => {
    e.preventDefault();
    if (!formule || !date || !time || !name || !phone || !email || (!isMoto && !vehicleType)) {
      toast.error("Merci de compléter la formule, le créneau et vos coordonnées.");
      return;
    }
    setLoading(true);
    try {
      const vLabel = isMoto ? "Moto" : VEHICLE_TYPES.find((v) => v.id === vehicleType)?.label || "";
      const res = await axios.post(`${API}/reservations`, {
        formule,
        vehicle_type: isMoto ? "" : vehicleType,
        vehicle_label: vLabel,
        options,
        date: format(date, "yyyy-MM-dd"),
        time,
        name,
        phone,
        email,
        vehicle_model: vehicleModel,
        message,
      });
      setDone(res.data);
      toast.success("Réservation envoyée ! Nous vous confirmons rapidement.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Une erreur est survenue. Réessayez ou appelez-nous.");
      if (date) fetchSlots(date);
    } finally {
      setLoading(false);
    }
  };

  const formuleName = ALL_FORMULES.find((f) => f.apiKey === formule)?.name;

  return (
    <section id="reservation" data-testid="booking-form-section" className="relative bg-[var(--bg-0)] py-24 sm:py-32 border-t border-white/10">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <span className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[var(--blue)]">[ Réservation ]</span>
          <h2 className="mt-4 font-display font-bold uppercase tracking-tighter text-4xl sm:text-5xl leading-none">
            Réservez
            <br />
            votre créneau
          </h2>
          <p className="mt-6 text-[var(--txt-1)] leading-relaxed">
            Choisissez votre formule, votre véhicule et un créneau disponible. Nous
            recevons votre demande instantanément et confirmons par téléphone.
          </p>

          {formule && (
            <div className="mt-8 rounded-md border border-[var(--blue)]/40 bg-[var(--bg-1)] p-6" data-testid="price-summary">
              <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Total estimé</span>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-mono2 text-5xl font-bold text-white">{price}</span>
                <span className="font-mono2 text-2xl text-[var(--blue)]">€</span>
              </div>
              <p className="mt-2 text-xs text-[var(--txt-2)]">
                {formuleName}
                {!isMoto && ` · ${VEHICLE_TYPES.find((v) => v.id === vehicleType)?.label}`}
                {options.length > 0 && ` · ${options.length} option(s)`}
              </p>
            </div>
          )}
        </div>

        <div className="lg:col-span-8">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="booking-success"
              className="rounded-md border border-[var(--blue)]/40 bg-[var(--bg-1)] p-12 flex flex-col items-start"
            >
              <div className="h-14 w-14 rounded-full bg-[var(--blue)] flex items-center justify-center mb-6">
                <Check size={26} className="text-white" />
              </div>
              <h3 className="font-display font-semibold text-2xl mb-3">Réservation envoyée !</h3>
              <p className="text-[var(--txt-1)] max-w-md">
                Merci {name}. Votre demande <b className="text-white">{formuleName}</b> le{" "}
                <b className="text-white">{date && format(date, "dd MMMM yyyy", { locale: fr })}</b> à{" "}
                <b className="text-white">{time}</b> (≈ {done.price}€) a bien été transmise. Nous vous rappelons très vite.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              {/* Formule */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Formule *</label>
                  <Select value={formule} onValueChange={setFormule}>
                    <SelectTrigger data-testid="booking-formule" className={`mt-2 ${field}`}>
                      <SelectValue placeholder="Choisir une formule" />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_FORMULES.map((f) => (
                        <SelectItem key={f.apiKey} value={f.apiKey}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">
                    {isMoto ? "Type" : "Type de véhicule *"}
                  </label>
                  {isMoto ? (
                    <div className={`mt-2 flex items-center gap-2 px-3 ${field}`}>
                      <Bike size={16} className="text-[var(--blue)]" /> Moto / deux-roues
                    </div>
                  ) : (
                    <Select value={vehicleType} onValueChange={setVehicleType}>
                      <SelectTrigger data-testid="booking-vehicle-type" className={`mt-2 ${field}`}>
                        <SelectValue placeholder="Type de véhicule" />
                      </SelectTrigger>
                      <SelectContent>
                        {VEHICLE_TYPES.map((v) => (
                          <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {/* Options */}
              <div>
                <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Options</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {OPTIONS.map((o) => {
                    const active = options.includes(o.label);
                    return (
                      <button
                        type="button"
                        key={o.label}
                        data-testid={`booking-option-${o.label}`}
                        onClick={() => toggleOption(o.label)}
                        className={`font-mono2 text-[11px] uppercase tracking-[0.1em] px-3 py-2 rounded-sm border transition-colors duration-200 ${
                          active
                            ? "bg-[var(--blue)] text-white border-[var(--blue)]"
                            : "text-[var(--txt-1)] border-white/15 hover:border-white/40"
                        }`}
                      >
                        {o.label} +{o.price}€
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Date + slots */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Date *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="booking-date"
                        className={`mt-2 w-full flex items-center gap-2 px-3 border ${field} ${date ? "text-white" : "text-[var(--txt-2)]"}`}
                      >
                        <CalendarIcon size={16} />
                        {date ? format(date, "dd MMMM yyyy", { locale: fr }) : "Choisir une date"}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) =>
                          d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-end">
                  <p className="font-mono2 text-[11px] uppercase tracking-[0.15em] text-[var(--txt-2)] pb-3">
                    Ouvert du lundi au samedi · 9h à 18h
                  </p>
                </div>
              </div>

              {/* Slot grid */}
              {date && (
                <div data-testid="slot-grid">
                  <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Créneau *</label>
                  {loadingSlots ? (
                    <div className="mt-3 flex items-center gap-2 text-[var(--txt-2)] text-sm">
                      <Loader2 size={16} className="animate-spin" /> Chargement des créneaux…
                    </div>
                  ) : closed ? (
                    <p className="mt-3 text-sm text-[var(--blue)]">Fermé ce jour, choisissez une autre date.</p>
                  ) : (
                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {slots.map((s) => {
                        const disabled = s.status !== "available";
                        const selected = time === s.time;
                        const labelMap = { booked: "Réservé", blocked: "Indispo", past: "Passé" };
                        return (
                          <button
                            type="button"
                            key={s.time}
                            data-testid={`slot-${s.time}`}
                            disabled={disabled}
                            onClick={() => setTime(s.time)}
                            className={`rounded-sm border px-2 py-2.5 text-center transition-colors duration-200 ${
                              selected
                                ? "bg-[var(--blue)] text-white border-[var(--blue)]"
                                : disabled
                                ? "border-white/5 bg-white/[0.02] text-[var(--txt-2)]/50 cursor-not-allowed line-through"
                                : "border-white/15 text-white hover:border-[var(--blue)]"
                            }`}
                          >
                            <span className="font-mono2 text-sm font-bold block">{s.time}</span>
                            {disabled && <span className="font-mono2 text-[9px] uppercase">{labelMap[s.status]}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2">
                <div>
                  <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Nom complet *</label>
                  <Input data-testid="booking-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jean Dupont" className={`mt-2 ${field}`} />
                </div>
                <div>
                  <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Téléphone *</label>
                  <Input data-testid="booking-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="06 12 34 56 78" className={`mt-2 ${field}`} />
                </div>
                <div>
                  <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Email *</label>
                  <Input data-testid="booking-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jean@email.com" className={`mt-2 ${field}`} />
                </div>
                <div>
                  <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Modèle du véhicule</label>
                  <Input data-testid="booking-vehicle" value={vehicleModel} onChange={(e) => setVehicleModel(e.target.value)} placeholder="Ex : Peugeot 208" className={`mt-2 ${field}`} />
                </div>
              </div>

              <div>
                <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Message</label>
                <Textarea data-testid="booking-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Précisions sur l'état du véhicule, demandes particulières…" className="mt-2 bg-transparent border-white/15 rounded-sm text-white placeholder:text-[var(--txt-2)] focus-visible:ring-0 focus-visible:border-[var(--blue)] min-h-[100px]" />
              </div>

              <button
                type="submit"
                data-testid="booking-submit"
                disabled={loading}
                className="group w-full inline-flex items-center justify-center gap-3 bg-white text-black font-mono2 text-xs font-bold uppercase tracking-[0.2em] px-8 py-5 rounded-sm hover:bg-[var(--blue)] hover:text-white transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Car size={16} />}
                Confirmer la réservation {price > 0 && `· ${price}€`}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
